import Autoload from '../../Autoload';
import { decodeHeadStatus, getHeadStatusSeverity } from '../../../helpers/smokeDetectorHeadStatus';

const { checkProperty } = Autoload.MainHelper;

// Real API shape (confirmed 2026-08-20 against live app.hellohousing.de responses):
// - GET meter-api/MeterReadingLogs?propertyId=...&offset=...&fetch=... — one row per automated
//   reading run, shared across ALL meter types: { id, readingId, readingDateTime, hasError,
//   errorCodes, meterNumbersFound, meterNumbersNotFound }. No smoke-detector-specific info here.
// - GET meter-api/readings/{readingId} — full detail for one run: { ...  meterReadings: [{
//   id, meter: { id, rentalUnitId, rentalUnitName, meterNo, location, allocationKey: { type, ... },
//   ... }, currentValue: { source, parsedValue }, meterReadingDate, ... }] }. meterReadings mixes
//   every meter type read in that run (water, heat, smoke detectors, ...) — must be filtered.
//
// Confirmed 2026-08-21 against a real property's smoke-detector meters (app.hellohousing.de):
// allocationKey.type === 'SMOKE_DETECTOR' identifies a smoke detector meter.
const SMOKE_DETECTOR_ALLOCATION_KEY_TYPE = 'SMOKE_DETECTOR';

class SmokeDetectorDataHandler {
  // Operates on a raw meter object (e.g. selectedMeter in ViewMeterReadingsForMeterModal.jsx),
  // as opposed to isSmokeDetectorMeterReading below, which operates on a meterReading wrapping one.
  static isSmokeDetectorMeter = (meter) => {
    const allocationKey = checkProperty(meter, 'allocationKey', {});
    return checkProperty(allocationKey, 'type') === SMOKE_DETECTOR_ALLOCATION_KEY_TYPE;
  };

  static isSmokeDetectorMeterReading = (meterReading) => {
    const meter = checkProperty(meterReading, 'meter', {});
    return SmokeDetectorDataHandler.isSmokeDetectorMeter(meter);
  };

  static mapDetector = (meterReading) => {
    const meter = checkProperty(meterReading, 'meter', {});
    const currentValue = checkProperty(meterReading, 'currentValue', 0);
    // currentValue is usually an object ({ source, parsedValue }), but for some readings
    // (confirmed 2026-08-21: automated/CSV-uploaded readings) the backend serializes it as a
    // bare number instead — read parsedValue only when currentValue is actually an object,
    // otherwise treat currentValue itself as the value. headStatus is always a 32-bit integer,
    // so round defensively before decoding either way.
    const rawValue = typeof currentValue === 'object' && currentValue !== null ? checkProperty(currentValue, 'parsedValue', 0) : currentValue;
    const rawHeadStatus = Math.round(rawValue);
    const decodedStatus = decodeHeadStatus(rawHeadStatus);

    return {
      detectorId: checkProperty(meter, 'id'),
      meterNo: checkProperty(meter, 'meterNo'),
      meterName: checkProperty(meter, 'meterName'),
      meterType: checkProperty(meter, 'meterType'), // 'PROPERTY' (Hauszähler) or 'RENTAL_UNIT' (Mieterzähler)
      rentalUnitId: checkProperty(meter, 'rentalUnitId'),
      rentalUnitName: checkProperty(meter, 'rentalUnitName'),
      location: checkProperty(meter, 'location'),
      rawHeadStatus,
      decodedStatus,
      severity: getHeadStatusSeverity(decodedStatus),
    };
  };

  // Combines one MeterReadingLogs row with its corresponding readings/{readingId} detail,
  // keeping only the smoke-detector meterReadings. Every MeterReadingLogs row is shown
  // chronologically regardless of whether that run touched any smoke detectors — a run with
  // none just renders as "0 Melder" rather than being hidden.
  static buildReadingSummary = (logRow, rawReadingDetail) => {
    const allMeterReadings = checkProperty(rawReadingDetail, 'meterReadings', []);
    const smokeDetectorReadings = allMeterReadings.filter(SmokeDetectorDataHandler.isSmokeDetectorMeterReading);

    const detectors = smokeDetectorReadings.map(SmokeDetectorDataHandler.mapDetector);
    // Sum of flagged conditions across all detectors, not the count of detectors that have at
    // least one — a single detector with 2 error fields (e.g. tamper + commsError) counts as 2,
    // not 1.
    const errorCount = detectors.reduce((sum, detector) => sum + detector.severity.errorFields.length, 0);
    const warningCount = detectors.reduce((sum, detector) => sum + detector.severity.warningFields.length, 0);
    // Property-wide meters (Hauszähler) get one shared section; rental-unit meters (Mieterzähler)
    // get grouped per unit — same split MasterData.js/MeterReadingRecord.js use for other meters.
    const propertyDetectors = detectors.filter((detector) => detector.meterType === 'PROPERTY');
    const rentalUnitDetectors = detectors.filter((detector) => detector.meterType !== 'PROPERTY');

    return {
      id: checkProperty(logRow, 'id'),
      readingId: checkProperty(logRow, 'readingId'),
      readingDateTime: checkProperty(logRow, 'readingDateTime'),
      detectors,
      propertyDetectors,
      rentalUnitGroups: SmokeDetectorDataHandler.groupByRentalUnit(rentalUnitDetectors),
      detectorsFound: detectors.length,
      hasError: errorCount > 0,
      hasWarning: warningCount > 0,
      errorCount,
      warningCount,
    };
  };

  static groupByRentalUnit = (detectors) => {
    const groups = {};

    (detectors || []).forEach((detector) => {
      const key = detector.rentalUnitId || 'unassigned';
      if (!groups[key]) {
        groups[key] = {
          rentalUnitId: detector.rentalUnitId,
          rentalUnitName: detector.rentalUnitName,
          detectors: [],
        };
      }
      groups[key].detectors.push(detector);
    });

    return Object.values(groups).sort((a, b) =>
      (a.rentalUnitName || '').localeCompare(b.rentalUnitName || '', 'de')
    );
  };
}

export default SmokeDetectorDataHandler;
